import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import MoreIcon from "@mui/icons-material/More";
import { DataGrid } from "@mui/x-data-grid";
import { addIdForDataGrid, applyColumnWidths, DG_FORMATTERS, disableHideableColumns } from "../../helpers/data-grid";
import { fetchDTNDataWithFilters } from "../../api/dtn-api-service";
import { formatCurrency } from "../../helpers/currency";
import { useQuery } from "@tanstack/react-query";
import DG_CONFIG from "../../config/data-grid";
import DraggableDialog from "../../common/components/DraggableDialog";
import Slider from "@mui/material/Slider";
import { getComponentPreference, setComponentPreference } from "../../common/services/cookie-service";
import { COLUMN_WIDTH_PREF_KEY } from "../../enums";

const DTN_POPUP_RACK = "dtnPopupRack";

const DTNPopup = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [brandType, setBrandType] = useState("U"); // Default to Unbranded
  const [rankThreshold, setRankThreshold] = useState(5); // Default: show ranks <= 5
  const [columnWidths, setColumnWidths] = useState(getComponentPreference(DTN_POPUP_RACK, COLUMN_WIDTH_PREF_KEY, {}));

  const {
    data: dtnGroupData,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["rackPricingDtnGroupWithFilter", row, brandType],
    queryFn: async () => {
      // Format date as yyyymmdd for dtN_EffDateInt
      const date = new Date(row.rackPricing_Date);
      const formattedDateInt =
        date.getFullYear().toString() +
        (date.getMonth() + 1).toString().padStart(2, "0") +
        date.getDate().toString().padStart(2, "0");

      const filterParams = {
        DTN_TerminalCity: row.rackPricing_Terminal_City,
        DTN_ProductID: row.rackPricing_FuelProduct_DTN_ProductID,
        dtN_EffDateInt: parseInt(formattedDateInt),
      };

      // Add brand type filter if not "All"
      if (brandType !== "All") {
        filterParams.dtN_BrandType = brandType;
      }

      const res = await fetchDTNDataWithFilters(filterParams);

      // Start with all records returned for the given city/product/date
      let filteredData = res.data || [];

      // Additional client-side brand type filtering if needed
      if (brandType !== "All") {
        filteredData = filteredData.filter(
          (item) => item.dtN_BrandType === brandType
        );
      }

      return addIdForDataGrid(filteredData);
    },
    enabled: false, // Disable automatic fetch
  });

  // Compute maximum rank from current data set to bound the slider
  const maxRank = useMemo(() => {
    const ranks = (dtnGroupData || [])
      .map((item) => item.dtN_DailyRank)
      .filter((r) => typeof r === "number");
    return ranks.length > 0 ? Math.max(...ranks) : 5;
  }, [dtnGroupData]);

  // Ensure the threshold stays within bounds if data changes
  useEffect(() => {
    if (rankThreshold > maxRank) {
      setRankThreshold(maxRank);
    }
  }, [maxRank, rankThreshold]);

  // Apply rank filtering client-side for display
  const visibleRows = useMemo(() => {
    const rows = dtnGroupData || [];
    return rows.filter((item) => {
      const rank = item.dtN_DailyRank;
      return typeof rank === "number" && rank >= 1 && rank <= rankThreshold;
    });
  }, [dtnGroupData, rankThreshold]);

  const isValidRow =
    row?.rackPricing_Terminal_City &&
    row?.rackPricing_FuelProduct_DTN_ProductID &&
    row?.rackPricing_Date;

  const handleClick = async () => {
    await refetch();
    setOpen(true);
  };

  const handleBrandTypeChange = (event, newBrandType) => {
    if (newBrandType !== null) {
      setBrandType(newBrandType);
    }
  };

  // Reset brand type to default when popup opens
  useEffect(() => {
    if (open) {
      setBrandType("U");
    }
  }, [open]);

  // Auto-refresh when brand type changes and dialog is open
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [brandType, open, refetch]);

  // Define DTN raw columns 
  const rawDtnColumns = useMemo(() => [
    {
      field: "dtN_UpdatedDate",
      headerName: "Date",
      width: 100,
      renderCell: (params) => DG_FORMATTERS.DATE(params.value),
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dtN_EffectiveTime",
      headerName: "Time",
      width: 80,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dtN_DailyRank",
      headerName: "Rank",
      width: 70,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "dtN_SupplierName",
      headerName: "Supplier",
      width: 150,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dtN_ProductName",
      headerName: "Product",
      width: 150,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dtN_TerminalName",
      headerName: "Terminal",
      width: 180,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dtN_PostedPrice",
      headerName: "Price",
      width: 100,
      renderCell: (params) => formatCurrency(params.value),
      headerAlign: "center",
      align: "right",
    },
    {
      field: "dtN_BrandType",
      headerName: "Brand",
      width: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const brandType = params.value;
        if (brandType === "B") return "Branded";
        if (brandType === "U") return "Unbranded";
        return brandType || "";
      },
    },
  ], []);

  const dtnColumns = useMemo(
    () => {
      const hideableColumns = disableHideableColumns(rawDtnColumns, ["dtN_UpdatedDate"]);
      return applyColumnWidths(hideableColumns, columnWidths);
    }, [rawDtnColumns, columnWidths]
  );

  const handleColumnResize = (params) => {
    const newWidth = params.width;
    const field = params.field || params.colDef?.field;

    if (!field || !newWidth || newWidth < 50) {
      console.error("DTNPopup (RackPricing): Invalid resize params!", { field, newWidth, params });
      return;
    }

    console.log(`DTNPopup (RackPricing): Column ${field} resized to ${newWidth}px`);

    setColumnWidths(prev => {
      const updated = { ...prev, [field]: newWidth };
      console.log("DTNPopup (RackPricing): Saving column widths:", updated);

      try {
        setComponentPreference(DTN_POPUP_RACK, COLUMN_WIDTH_PREF_KEY, updated);
        console.log("DTNPopup (RackPricing): Column width preferences saved successfully");
      } catch (error) {
        console.error("DTNPopup (RackPricing): Failed to save column width preferences:", error);
      }

      return updated;
    });
  };

  return (
    <>
      <Tooltip
        title={
          isValidRow
            ? "View DTN Data"
            : "Missing required DTN information. Terminal City or Product ID is not available."
        }
      >
        <span>
          <MoreIcon
            style={{ cursor: isValidRow ? "pointer" : "not-allowed" }}
            onClick={isValidRow ? handleClick : undefined}
            color={isValidRow ? "primary" : "disabled"}
          />
        </span>
      </Tooltip>

      {open && (
        <DraggableDialog
          onClose={() => setOpen(false)}
          title={
            row
              ? `${row.rackPricing_Terminal_City} - ${row.rackPricing_FuelProduct_RackDesc}`
              : "DTN Data"
          }
          maxWidth="lg"
          fullWidth
          refreshBtn={true}
          isDataGrid={true}
          onRefresh={() => refetch()}
        >
          <DialogContent dividers sx={{ position: "relative", pb: 8 }}>
            <div
              style={{
                height: "400px",
                width: "100%",
              }}
            >
              <DataGrid
                rows={visibleRows || []}
                columns={dtnColumns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                  sorting: {
                    sortModel: [{ field: "dtN_DailyRank", sort: "asc" }],
                  },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
                loading={isFetching}
                autosizeOptions={{
                  columns: ['dtN_SupplierName', 'dtN_ProductName', 'dtN_TerminalName'],
                  includeHeaders: true,
                  includeOutliers: true,
                }}
                autosizeOnMount
                disableColumnResize={false}
                onColumnResize={handleColumnResize}
                slotProps={{
                  loadingOverlay: DG_CONFIG.OVERLAY,
                }}
                sx={{
                  '& .MuiDataGrid-root': {
                    fontSize: '0.75rem',
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.75rem',
                    padding: '4px 8px',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '4px 8px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }
                }}
              />
            </div>

            {/* Brand Type Filter */}
            <Box
              sx={{
                position: "absolute",
                bottom: 8,
                left: 16,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Brand Type:
              </Typography>
              <ToggleButtonGroup
                value={brandType}
                exclusive
                onChange={handleBrandTypeChange}
                aria-label="brand type filter"
                size="small"
              >
                <ToggleButton value="B" aria-label="branded">
                  Branded
                </ToggleButton>
                <ToggleButton value="U" aria-label="unbranded">
                  Unbranded
                </ToggleButton>
                <ToggleButton value="All" aria-label="all">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
              {/* Rank Filter Slider */}
              <Typography
                id="rank-filter-label"
                variant="body2"
                sx={{ fontWeight: 500, ml: 3 }}
              >
                Max Rank:
              </Typography>
              <Box sx={{ width: 200 }}>
                <Slider
                  aria-labelledby="rank-filter-label"
                  value={Math.min(rankThreshold, maxRank)}
                  min={1}
                  max={Math.max(1, maxRank)}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_e, value) => {
                    if (typeof value === "number") setRankThreshold(value);
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
        </DraggableDialog>
      )}
    </>
  );
};

DTNPopup.propTypes = {
  row: PropTypes.object.isRequired,
};

export default DTNPopup;
